import { createSelector } from '@reduxjs/toolkit';
import { DefaultRootState } from 'react-redux';
import { isEmpty } from 'lodash';
import { BooleanPreferences, Preferences } from '../types/preferences.type';
import { annotationTaskSettingsSelector } from '../../ecg/selectors/annotation-task.selectors';

export const authSelector = ({ auth }: DefaultRootState) => auth;

export const userSelector = createSelector(authSelector, ({ user }) => user);

export const isLoggedInSelector = createSelector(
  userSelector,
  (user) => user != null,
);

export const userNameSelector = createSelector(
  userSelector,
  (user) => user?.name,
);

export const qualityRoleSelector = createSelector(
  userSelector,
  (user) => user?.qualityRole,
);

export const isAdminSelector = createSelector(
  userSelector,
  (user) => user?.role === 'admin',
);

export const reprocessingEnabledSelector = createSelector(
  userSelector,
  (user) => user != null && user.role === 'admin',
);

export const workflowDisabledSelector = createSelector(
  userSelector,
  (user) => user?.organization?.name !== 'Datask Force',
);

export const preferencesSelector = createSelector(
  authSelector,
  annotationTaskSettingsSelector,
  ({ preferences }, annotationTaskSettingsSelector) => {
    if (isEmpty(preferences)) {
      return null;
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return { ...preferences!, ...annotationTaskSettingsSelector };
  },
);

export const coworkersSelector = createSelector(
  authSelector,
  ({ coworkers }) => coworkers,
);

export const coworkerNamesSelector = createSelector(
  coworkersSelector,
  (coworkers) => new Set(coworkers?.map(({ name }) => name)),
);

export const showShrSelector = createSelector(
  preferencesSelector,
  (preferences) => preferences?.showShr ?? false,
);

export const booleanPreferenceSelector = (key: BooleanPreferences) =>
  createSelector(preferencesSelector, (preferences) =>
    preferences != null ? preferences[key] : false,
  );

export const allowedWavesSelector = (
  key: Extract<'wavesToDisplay' | 'wavesToAnnotate', keyof Preferences>,
) =>
  createSelector(preferencesSelector, (preferences) =>
    preferences != null ? preferences[key] : new Set([]),
  );

export const allowedTagsSelector = (
  key: Extract<'tagsToDisplay' | 'tagsToAnnotate', keyof Preferences>,
) =>
  createSelector(preferencesSelector, (preferences) =>
    preferences != null ? preferences[key] : { P: [], QRS: [] },
  );

export const annotationTableSelector = createSelector(
  preferencesSelector,
  (preferences) => preferences?.annotationTable,
);
